import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  Output,
  EventEmitter,
} from "@angular/core";
import { ofType, Actions } from "@ngrx/effects";
import { map, take, takeUntil } from "rxjs/operators";
import {
  InvoiceReportsSearchParams,
  Invoice,
} from "src/app/shared/models/invoices.model";
import { ReportsService } from "src/app/shared/services/reports.service";
import { ServiceMetaData } from "src/app/shared/models/service-meta.model";
import { PaginationParams } from "src/app/shared/models/common";
import { AuthService } from "src/app/shared/services/auth.service";
import { Subject } from "rxjs";
import { Store } from "@ngrx/store";
import { RUN_REPORTS_SEARCH } from "src/app/shared/ngrx/ngrx-actions";
import {
  curReportInvoices,
  invoicesSearchMetadata,
  reportsSearchParams,
} from "src/app/shared/ngrx/ngrx-selectors";
import { CommonModule } from "@angular/common";
import { LoaderComponent } from "src/app/shared/components/ui/loader/loader.component";
import { TableComponent } from "src/app/shared/components/ui/table/table.component";
import { SpinnerComponent } from "src/app/shared/components/ui/spinner/spinner.component";

@Component({
  selector: "invoices",
  templateUrl: "./invoices.component.html",
  styleUrls: ["./invoices.component.sass"],
  standalone: true,
  imports: [CommonModule, LoaderComponent, TableComponent, SpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoicesComponent implements OnInit {
  destroy$ = new Subject();
  searchParams: InvoiceReportsSearchParams;
  numberOfRows: number = 10;
  loading: boolean;
  docLoading: boolean;
  clinicUserSw: boolean;

  @Output() onDocPdfOpen = new EventEmitter<any>();

  get reportsSearchParams(): InvoiceReportsSearchParams {
    return this.store.selectSignal(reportsSearchParams)();
  }

  get searchMetaData(): ServiceMetaData {
    return this.store.selectSignal(invoicesSearchMetadata)() || {};
  }

  get data(): Invoice[] {
    return this.store.selectSignal(curReportInvoices)();
  }

  constructor(
    private readonly store: Store,
    private reportsService: ReportsService,
    private authService: AuthService,
    private actions$: Actions,
    private cdr: ChangeDetectorRef
  ) {
    this.clinicUserSw = this.authService.currentUser.org == "CLINIC";

    this.actions$
      .pipe(
        ofType(RUN_REPORTS_SEARCH),
        takeUntil(this.destroy$),
        map(({}) => {
          this.runSearch(true);
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    if (this.store.selectSignal(reportsSearchParams)()) {
      this.runSearch(true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(1);
  }

  runSearch(newSearch: boolean) {
    if (newSearch) {
      this.searchParams = {
        rangeTo: this.numberOfRows,
        rangeFrom: 1,
        searchId: null,
      };
    }

    this.searchParams.clinic = this.reportsSearchParams.clinic;
    this.searchParams.documentId = this.reportsSearchParams.documentId;
    this.searchParams.fromDate = this.reportsSearchParams.fromDate;
    this.searchParams.toDate = this.reportsSearchParams.toDate;

    this.loading = true;
    this.cdr.markForCheck();
    this.reportsService.getClientInvoices(this.searchParams).subscribe(
      () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
      () => {
        this.loading = false;
        this.cdr.markForCheck();
      }
    );
  }

  loadDataOnPagination(event: PaginationParams) {
    if (
      !this.data ||
      (event.rangeFrom == this.searchParams.rangeFrom &&
        event.rangeTo == this.searchParams.rangeTo)
    )
      return;

    this.searchParams.rangeFrom = event.rangeFrom;
    this.searchParams.rangeTo = event.rangeTo;
    this.searchParams.searchId = this.searchMetaData.searchId;

    this.runSearch(false);
  }

  onRowSelect(invoice: Invoice) {
    invoice.docLoading = true;
    this.reportsService.getInvoicePdfUrl(invoice.invId).subscribe(
      (data) => {
        if (data) {
          invoice.docLoading = false;
          this.cdr.markForCheck();
          this.onDocPdfOpen.emit({ docNum: invoice.invId, url: data });
        }
      },
      (err) => {
        invoice.docLoading = false;
        this.cdr.markForCheck();
      }
    );
  }
}
